import {
  type AdminTicketListItem,
  type AssignTicketBody,
  adminTicketsPage,
  type BulkTicketActionBody,
  type BulkTicketActionResponse,
  bulkTicketActionResponseDto,
  type CloseTicketBody,
  type ListTicketsQuery,
  type TicketDetailDto,
  ticketDetailDto,
} from '@planuze/admin-contracts';
import { ApiError, parseJson, readErrorCode, request } from './http.js';

/* Cliente REST de tickets de suporte (28-14, ADR-0352). List/detail/assign/close são
   REST; o envio de mensagem + realtime é via WebSocket (use-chat-websocket). Todas as
   facetas da inbox são server-side (ADR-0352): filtrar client-side perdia itens nas
   páginas ainda não carregadas. */

export interface TicketsQuery {
  readonly status?: ListTicketsQuery['status'];
  readonly priority?: ListTicketsQuery['priority'];
  readonly assignment?: ListTicketsQuery['assignment'];
  readonly search?: string | undefined;
  readonly createdFrom?: string | undefined;
  readonly createdTo?: string | undefined;
  readonly segment?: ListTicketsQuery['segment'];
  readonly cursor?: string | undefined;
}

const HTTP_NOT_FOUND = 404;

async function ticketError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP_NOT_FOUND) {
    return new ApiError(res.status, 'not_found', code || 'ticket not found');
  }
  return new ApiError(res.status, 'server_error', code || 'request failed');
}

function buildQuery(query: TicketsQuery): string {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set('status', query.status);
  if (query.priority !== undefined) params.set('priority', query.priority);
  if (query.assignment !== undefined && query.assignment !== 'all') {
    params.set('assignment', query.assignment);
  }
  if (query.search !== undefined && query.search.length > 0) params.set('search', query.search);
  if (query.createdFrom !== undefined) params.set('createdFrom', query.createdFrom);
  if (query.createdTo !== undefined) params.set('createdTo', query.createdTo);
  if (query.segment !== undefined) params.set('segment', query.segment);
  if (query.cursor !== undefined && query.cursor.length > 0) params.set('cursor', query.cursor);
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function listTickets(
  query: TicketsQuery,
): Promise<{ readonly items: readonly AdminTicketListItem[]; readonly nextCursor: string | null }> {
  const res = await request(`/v1/admin/tickets${buildQuery(query)}`);
  if (!res.ok) throw await ticketError(res);
  const page = await parseJson(res, adminTicketsPage);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

export async function getTicket(id: string): Promise<TicketDetailDto> {
  const res = await request(`/v1/admin/tickets/${encodeURIComponent(id)}`);
  if (!res.ok) throw await ticketError(res);
  return parseJson(res, ticketDetailDto);
}

export async function assignTicket(id: string, body: AssignTicketBody): Promise<void> {
  const res = await request(`/v1/admin/tickets/${encodeURIComponent(id)}/assign`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await ticketError(res);
}

export async function closeTicket(id: string, body: CloseTicketBody): Promise<void> {
  const res = await request(`/v1/admin/tickets/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await ticketError(res);
}

export async function bulkTicketAction(
  body: BulkTicketActionBody,
): Promise<BulkTicketActionResponse> {
  const res = await request('/v1/admin/tickets/bulk', { method: 'POST', body });
  if (!res.ok) throw await ticketError(res);
  return parseJson(res, bulkTicketActionResponseDto);
}
